// Numbers found in OCR text, in reading order, without duplicates. A comma is read as decimal separator.
export function parseNumbers(text: string): number[] {
    const numbers = (text.match(/\d+(?:[.,]\d+)?/g) ?? []).map(n => parseFloat(n.replace(',', '.')));
    // 8+ digit numbers are ids or barcodes, no odometer or pump reading is that long
    return [...new Set(numbers)].filter(n => n > 0 && n < 1e7);
}

// A receipt prints the unit next to the quantity ("62,46 Liter"); the price per litre is "1,569 EUR/Liter" and does not match.
export function litersOnReceipt(text: string): number | undefined {
    const match = text.match(/(\d+[.,]\d+)\s*(?:l|ltr|liter|litre)\b/i);
    return match ? parseFloat(match[1].replace(',', '.')) : undefined;
}

// A pump display shows quantity × price = total; when such a triple is found, its quantity goes first.
export function quantityFirst(numbers: number[]): number[] {
    for (const quantity of numbers)
        for (const price of numbers) {
            // prices always have decimals, which also keeps integer pairs like 9 × 11 ≈ 98 out
            if (price >= quantity || price % 1 === 0)
                continue;
            const total = quantity * price;
            if (numbers.some(n => n !== quantity && n !== price && Math.abs(n - total) <= 0.01 * total))
                return [quantity, ...numbers.filter(n => n !== quantity)];
        }
    return numbers;
}

// Reads the text of a photo in the browser. tesseract.js is loaded on first use to keep it out of the main bundle.
// Worker, wasm core and language data come from our own server (/ocr, emitted by webpack), never from a CDN (DSGVO).
export async function readText(image: File): Promise<string> {
    const { createWorker, OEM, PSM } = await import('tesseract.js');
    const worker = await createWorker('eng', OEM.LSTM_ONLY, { workerPath: '/ocr/worker.min.js', corePath: '/ocr/tesseract-core-simd-lstm.wasm.js', langPath: '/ocr' });
    try {
        // no digit whitelist: dropping letters glues neighbouring numbers together ("62,46 Liter ... 6" became 62.466)
        await worker.setParameters({
            // dashboards and pump displays have scattered numbers, not lines of text
            tessedit_pageseg_mode: PSM.SPARSE_TEXT,
        });
        const { data } = await worker.recognize(image);
        return data.text;
    } finally {
        await worker.terminate();
    }
}
